import { type ColumnInfo, type StoreSchema, api } from "../lib/api.ts";
import { useLoad } from "../lib/useLoad.ts";
/**
 * The data dictionary. Both halves are read from the stores' own catalogs, so
 * this page cannot drift from what is actually deployed — a table added without
 * a description shows up here with an empty purpose, which is the point.
 */
import { Card, InfoBanner, Page, Table } from "../ui/index.ts";

export function System() {
	const [schema] = useLoad(() => api.schema());

	return (
		<Page
			title="Where everything is stored"
			description="Read live from the databases themselves. Operational records live in CloudSQL; spend and delivery history belong in BigQuery. Column purpose comes from the schema comments, so it stays true as the schema moves."
		>
			{schema.state === "loading" && <p className="text-sm text-muted">Reading catalogs…</p>}
			{schema.state === "failed" && (
				<InfoBanner variant="danger" title="The schema could not be read.">
					{schema.why}
				</InfoBanner>
			)}
			{schema.state === "ready" && (
				<div className="space-y-10">
					<Store store={schema.data.cloudsql} />
					<Store store={schema.data.bigquery} />
				</div>
			)}
		</Page>
	);
}

function Store({ store }: { store: StoreSchema }) {
	return (
		<section>
			<p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-muted">{store.label}</p>
			{store.error && (
				<InfoBanner variant="warning" title="Not readable right now.">
					{store.error}
				</InfoBanner>
			)}
			{!store.error && store.tables.length === 0 && (
				<p className="text-sm text-muted">No tables yet.</p>
			)}
			<div className="space-y-6">
				{store.tables.map((t) => (
					<Card key={t.name} className="p-5">
						<div className="mb-1 flex items-baseline justify-between gap-3">
							<h3 className="font-mono font-semibold text-content">{t.name}</h3>
							{t.rows !== null && (
								<span className="text-xs text-muted">~{t.rows.toLocaleString()} rows</span>
							)}
						</div>
						<p
							className={`mb-3 max-w-[74ch] text-sm ${t.description ? "text-muted" : "text-warning"}`}
						>
							{t.description ??
								"No description on this table — add a COMMENT ON so it explains itself here."}
						</p>
						<Table<ColumnInfo>
							columns={[
								{
									id: "name",
									header: "Column",
									accessor: (c) => (
										<span className="font-mono text-xs">
											{c.name}
											{!c.nullable && <span className="ml-0.5 text-primary">*</span>}
										</span>
									),
								},
								{
									id: "type",
									header: "Type",
									accessor: (c) => <span className="font-mono text-xs text-muted">{c.type}</span>,
								},
								{
									id: "desc",
									header: "What it holds",
									accessor: (c) => (
										<span className={c.description ? "" : "text-muted"}>
											{c.description ?? "—"}
										</span>
									),
								},
							]}
							data={t.columns}
							keyExtractor={(c) => c.name}
						/>
						{t.constraints.length > 0 && (
							<div className="mt-4 border-t border-line pt-3">
								<p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-muted">
									Constraints
								</p>
								<ul className="space-y-1.5">
									{t.constraints.map((con) => (
										<li key={con.name} className="text-xs">
											<span className="font-mono text-content">{con.name}</span>
											<span className="ml-2 text-muted">{con.kind}</span>
											<div className="mt-0.5 max-w-[100ch] overflow-x-auto">
												<code className="whitespace-pre text-[11px] text-muted">
													{con.definition}
												</code>
											</div>
										</li>
									))}
								</ul>
							</div>
						)}
						{t.indexes.length > 0 && (
							<div className="mt-4 border-t border-line pt-3">
								<p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-muted">
									Indexes
								</p>
								<ul className="space-y-1.5">
									{t.indexes.map((i) => (
										<li key={i.name} className="text-xs">
											<span className="font-mono text-content">{i.name}</span>
											{i.primary && <span className="ml-2 text-muted">primary key</span>}
											{i.unique && !i.primary && <span className="ml-2 text-primary">unique</span>}
											{/*
											 * The definition, not a summary of it. A partial unique
											 * index enforces exactly what its predicate says, and
											 * `tiktok_account_single_tenant` — one ad account, one team —
											 * is the whole of this project's tenant isolation.
											 */}
											<div className="mt-0.5 max-w-[100ch] overflow-x-auto">
												<code className="whitespace-pre text-[11px] text-muted">
													{i.definition}
												</code>
											</div>
										</li>
									))}
								</ul>
							</div>
						)}
					</Card>
				))}
			</div>
		</section>
	);
}
